import {
  BrowserPlatformLocation,
  LocationChangeListener,
  PlatformLocation,
} from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  Injectable,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

import { provideApollo } from 'apollo-angular';

import { createApolloProvider } from '@shared/lib/graphql';
import { authInterceptor, errorInterceptor } from '@shared/lib/interceptors';
import { appResolver } from '@shared/lib/resolvers';

import { routes } from './app.routes';
import { PortalErrorHandler } from './error-handler';
import { environment } from '../environments/environment';

/**
 * Drops single-spa's synthetic popstate (fired on every pushState/replaceState)
 * before it reaches Angular Router. A capture-phase window listener can't do
 * this reliably: once any single-spa copy has started, it hijacks
 * window.addEventListener and replays popstate listeners itself, bypassing
 * stopImmediatePropagation.
 */
@Injectable()
class SingleSpaAwarePlatformLocation extends BrowserPlatformLocation {
  override onPopState(fn: LocationChangeListener): VoidFunction {
    return super.onPopState(event => {
      if ((event as { singleSpa?: boolean }).singleSpa) {
        return;
      }
      fn(event);
    });
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: PlatformLocation, useClass: SingleSpaAwarePlatformLocation },
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: PortalErrorHandler },
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
    ),
    provideAppInitializer(appResolver),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideApollo(() =>
      createApolloProvider({
        httpLinkOptions: {
          uri: environment.unifiedPortalGraphQl.baseUrl,
        },
        options: {
          customHeaders: {
            'Ocp-Apim-Subscription-Key':
              environment.unifiedPortalGraphQl.subscriptionKey,
          },
        },
      }),
    ),
  ],
};
